import React, { useEffect, useState, useRef } from 'react';
import { connect } from 'react-redux';
import debounce from 'lodash.debounce';
import {
  Card,
  Table,
  Tooltip,
  Row,
  Col,
  Radio,
  Icon,
} from 'antd';
import queryString from 'query-string';
import { useLocation } from 'react-router-dom';
import moment from 'moment-timezone';
import Loading from 'components/Loading';
import RBAC from 'components/rbac';

import {
  manageAccountActions,
  notificationActions,
  filterParamsActions,
} from 'store/actions';
import { FILTERS_SPECS } from 'store/actionTypes';
import {
  PAGES,
  COUNTRIES,
  BENE_TYPES,
  ROLES,
} from 'appconstants';
import {
  SpButton,
} from 'components/DesignKit';
import AddAccont from './components/AddAccont';
import DeleteAccount from './components/DeleteAccount';
import DefaultAccount from './components/DefaultAccount';
import ConfirmPopup from './components/ConfirmPopup';
import BankLogo from './components/BankLogo';


type Props = {
  fetchSelfAccounts: Function,
  manageAccountsList: Array<Object>,
  loading: boolean,
  test: boolean,
  history: {
    push: Function,
  };
  totalElements: number,
  selectedAccount: Object,
  deleteSelefAccount: Function,
  submitting: Boolean,
  addSelfAccount: Function,
  addBusinessBankAccountAsPrimary: Function,
  updateSelfAccount: Function,
};

const ManageAccounts = (props: Props) => {
  const location = useLocation();
  const {
    loading,
    fetchSelfAccounts,
    manageAccountsList,
    test,
    history,
    totalElements,
    selectedAccount,
    deleteSelefAccount,
    submitting,
    addSelfAccount,
    addBusinessBankAccountAsPrimary,
    updateSelfAccount,
  } = props;
  const [pagination, setPagination] = useState({});
  const myRef = useRef();
  // eslint-disable-next-line no-unused-vars
  const [open, setOpen] = useState(false);
  const [addAccontModal, setAddAccountModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedRecordAccount, setSelectedRecordAccount] = useState();
  const [showDefaultModal, setShowDefaultModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [newBankInfo, setNewBankInfo] = useState({});
  const isIndia = (selectedAccount && selectedAccount.country && selectedAccount.country.id) === COUNTRIES.INDIA.id;
  const debouncedFetchSelfAccounts = debounce(fetchSelfAccounts, 2000, { leading: true });

  const getData = (query) => {
    const parsed = queryString.parse(query);
    const currentPage = parsed.pageNum || 1;
    const sortBy = parsed.sortBy || 'createdAt';
    const direction = parsed.direction || 'DESC';
    const sortParams = `&sortBy=${sortBy}&direction=${direction}`;
    const filterParam = parsed.filterParams || '';
    debouncedFetchSelfAccounts({ currentPage, sortParams, filterParam });
  };

  const selectedRecord = (record) => {
    const searchParams = record.gid;
    history.push({
      pathname: `${PAGES.MANAGE_ACCOUNTS}/${searchParams}`,
      state: location.search,
    });
  };

  useEffect(() => {
    getData(location.search);
  }, [location, test, selectedAccount]);

  useEffect(() => {
    if (!submitting) {
      setShowDeleteModal(false);
      setAddAccountModal(false);
      setShowDefaultModal(false);
      setShowConfirmModal(false);
      setNewBankInfo({});
    }
  }, [submitting]);

  const handleClick = e => {
    if (myRef.current && myRef.current.contains(e.target)) {
      return;
    }
    if (e.target.value === undefined
      && e.toElement.className !== 'ant-calendar-date'
      && e.toElement.className !== 'ant-calendar-prev-month-btn'
      && e.toElement.className !== 'ant-calendar-next-month-btn') {
      setOpen(false);
    }
  };

  useEffect(() => {
    const parsed = queryString.parse(location.search);
    const currentPage = parsed.pageNum || 1;
    setPagination(existingElements => ({
      ...existingElements,
      total: totalElements,
      current: parseInt(currentPage, 10),
      showTotal: total => `Total ${total} items`,
    }));
  }, [manageAccountsList]);

  useEffect(() => {
    document.addEventListener('mousedown', handleClick);
    return () => {
      document.removeEventListener('mousedown', handleClick);
    };
  }, []);

  const handleTableChange = (page, sorter, filters) => {
    const tempPage = page.current;
    const parsed = queryString.parse(location.search);
    const searchParam = parsed['status.IN'];
    const filterParams = parsed.filterParams || '';
    let tempParams = '';
    if (filters.order === 'ascend') {
      tempParams = `&sortBy=${filters.field}&direction=ASC`;
    } else if (filters.order === 'descend') {
      tempParams = `&sortBy=${filters.field}&direction=DESC`;
    } else {
      tempParams = '';
    }
    if (location.search && searchParam && !filterParams) {
      history.push({
        pathname: PAGES.MANAGE_ACCOUNTS,
        search: `?status.IN=${searchParam}&pageNum=${tempPage}${tempParams}`,
      });
    } else if (!searchParam && !filterParams) {
      history.push({
        pathname: PAGES.MANAGE_ACCOUNTS,
        search: `?pageNum=${tempPage}${tempParams}`,
      });
    } else if (searchParam && filterParams) {
      const filterParam = encodeURIComponent(filterParams);
      history.push({
        pathname: PAGES.MANAGE_ACCOUNTS,
        search: `?status.IN=${searchParam}&filterParams=${filterParam}&pageNum=${tempPage}${tempParams}`,
      });
    } else if (!searchParam && filterParams) {
      const filterParam = encodeURIComponent(filterParams);
      history.push({
        pathname: PAGES.MANAGE_ACCOUNTS,
        search: `?filterParams=${filterParam}&pageNum=${tempPage}${tempParams}`,
      });
    }
  };

  const setDeleteAccount = (record) => {
    setSelectedRecordAccount(record);
    setShowDeleteModal(true);
  };

  const setDefaultAccont = (record) => {
    setSelectedRecordAccount(record);
    setShowDefaultModal(true);
  };

  const setDefaultContact = (record) => {
    const parsed = queryString.parse(location.search);
    const currentPage = parsed.pageNum || 1;
    const sortBy = parsed.sortBy || 'createdAt';
    const direction = parsed.direction || 'DESC';
    const sortParams = `&sortBy=${sortBy}&direction=${direction}`;
    const filterParam = parsed.filterParams || '';
    const payload = { currentPage, sortParams, filterParam };
    const contactGid = record.contact.gid;
    updateSelfAccount({ payload, contactGid });
  };

  const columns = [
    {
      title: 'NICK NAME',
      dataIndex: 'nickName',
      width: '12%',
      align: 'left',
      sorter: false,
      sortDirections: ['ascend', 'descend', 'ascend'],
      render: (nickName) => (
        <Tooltip placement="top" title="Click here to view details">
          <span>{nickName || <>&#8211;</>}</span>
        </Tooltip>
      ),
    },
    {
      title: 'BANK NAME',
      dataIndex: 'bankName',
      width: '15%',
      align: 'left',
      sorter: true,
      sortDirections: ['ascend', 'descend', 'ascend'],
      render: (text, record) => (
        <Tooltip placement="top" title="Click here to view details">
          <span>{record.issuerBank.bankName}</span>
        </Tooltip>
      ),
    },
    {
      title: 'ACCOUNT NO.',
      dataIndex: 'issuerBank',
      width: '15%',
      align: 'left',
      render: (issuerBank, record) => {
        const bankName = ((record.issuerBank) && (record.issuerBank.bankName));
        const lastFour = ((record.issuerBank) && (record.issuerBank.lastFour));
        if ((bankName) && (lastFour)) {
          return (
            <span style={{ cursor: 'pointer' }}>
              <BankLogo bankName={bankName} /> ****{lastFour}
            </span>
          );
        } if (lastFour) {
          return (
            <span style={{ cursor: 'pointer' }}>
              {`****${lastFour}`}
            </span>
          );
        } return (
          <span style={{ cursor: 'pointer' }}>
            <>&#8211;</>
          </span>
        );
      },
    },
    {
      title: 'ROUTING NUMBER',
      dataIndex: 'issuerBank',
      width: '15%',
      align: 'left',
      sorter: true,
      sortDirections: ['ascend', 'descend', 'ascend'],
      render: (text, record) => (
        <Tooltip placement="top" title="Click here to view details">
          <span>{record.issuerBank.routingNumber}</span>
        </Tooltip>
      ),
    },
    {
      title: 'CREATED DATE',
      dataIndex: 'createdAt',
      width: '13%',
      align: 'left',
      render: (createdAt) => {
        if (createdAt && selectedAccount && selectedAccount.timezone) {
          const tzDate = moment
            .utc(createdAt)
            .tz(selectedAccount.timezone)
            .format('MMM DD, YYYY');
          return (
            <Tooltip placement="top" title="Click here to view details">
              <span>{tzDate}</span>
            </Tooltip>
          );
        }
        return (
          <Tooltip placement="top" title="Click here to view details">
            <span>&#8211;</span>
          </Tooltip>
        );
      },
      sorter: true,
      defaultSortOrder: 'descend',
      sortDirections: ['ascend', 'descend', 'ascend'],
    },
    {
      title: 'PRIMARY A/C',
      dataIndex: 'primary',
      width: '10%',
      align: 'center',
      render: (text, record) => {
        const { status } = record;
        if (status === 'VERIFIED') {
          return (
            <Radio
              onClick={(e) => { e.stopPropagation(); setDefaultAccont(record); }}
              checked={record.contact.primary}
            />
          );
        }
        return (
          <Radio
            disabled
            checked={record.contact.primary}
          />
        );
      },
    },
    {
      title: 'ACTION',
      dataIndex: 'delete',
      width: '13%',
      align: 'center',
      render: (text, record) => (
        <span>
          <Tooltip placement="top" title="Delete">
            <Icon
              type="delete"
              style={{ fontSize: '20px', marginRight: '8px', cursor: 'pointer' }}
              onClick={(e) => { e.stopPropagation(); setDeleteAccount(record); }}
            />
          </Tooltip>
        </span>
      ),
    },
  ];

  const addNewBankAccount = (transferInfo) => {
    setNewBankInfo(transferInfo);
    setShowConfirmModal(true);
    setAddAccountModal(false);
  };

  const createNewBankAccount = () => {
    const currentPage = 1;
    const sortBy = 'createdAt';
    const direction = 'DESC';
    const sortParams = `&sortBy=${sortBy}&direction=${direction}`;
    const filterParam = '';
    const payload = { currentPage, sortParams, filterParam };
    addSelfAccount({ payload, data: newBankInfo });
  };

  const createBusinessAccountAsPrimary = () => {
    const currentPage = 1;
    const sortBy = 'createdAt';
    const direction = 'DESC';
    const sortParams = `&sortBy=${sortBy}&direction=${direction}`;
    const filterParam = '';
    const payload = { currentPage, sortParams, filterParam };
    addBusinessBankAccountAsPrimary({ payload });
  };

  const deleteSelectedAccount = (data) => {
    const parsed = queryString.parse(location.search);
    const currentPage = parsed.pageNum || 1;
    const sortBy = parsed.sortBy || 'createdAt';
    const direction = parsed.direction || 'DESC';
    const sortParams = `&sortBy=${sortBy}&direction=${direction}`;
    const filterParam = parsed.filterParams || '';
    const payload = { currentPage, sortParams, filterParam };
    deleteSelefAccount({ payload, data });
  };

  if (loading || !selectedAccount) {
    return <Loading />;
  }

  return (
    <Card>
      <Row className="my-2">
        <div className="d-flex justify-content-end">
          <RBAC
            permit={[
              ROLES.ADMINISTRATOR,
              ROLES.OWNER,
              ROLES.DEVELOPER,
              ROLES.OPERATIONS,
              // ROLES.ANALYST,
              ROLES.SUPPORT,
              ROLES.INTERNALSUPPORT,
              ROLES.POS,
              ROLES.SUPERUSER,
              ROLES.ONBOARDING,
              ROLES.TRANSFERAPPROVER,
              // ROLES.TRANSFERRECON,
              ROLES.TRANSFERSUPPORT,
            ]}
          >
            <Col>
              <SpButton
                type="secondary"
                shape="round"
                className="mr-3"
                ghost
                onClick={createBusinessAccountAsPrimary}
              >
                {'\u002B'}&nbsp;Create Bank From Business
              </SpButton>
            </Col>
            <Col>
              <SpButton
                type="secondary"
                shape="round"
                className="mr-3"
                ghost
                onClick={() => setAddAccountModal(true)}
              >
                {'\u002B'}&nbsp;Add Bank Account
              </SpButton>
            </Col>
          </RBAC>
        </div>
      </Row>
      <AddAccont
        close={() => setAddAccountModal(false)}
        submit={addNewBankAccount}
        visible={addAccontModal}
        selectedAccount={selectedAccount}
        country={selectedAccount.country.name}
        cntryId={selectedAccount.country.id}
        isIndia={isIndia}
      />

      <DefaultAccount
        close={() => setShowDefaultModal(false)}
        submit={setDefaultContact}
        visible={showDefaultModal}
        selectedRecordAccount={selectedRecordAccount}
      />

      <DeleteAccount
        close={() => setShowDeleteModal(false)}
        submit={deleteSelectedAccount}
        visible={showDeleteModal}
        selectedRecordAccount={selectedRecordAccount}
      />

      <ConfirmPopup
        close={() => setShowConfirmModal(false)}
        submit={createNewBankAccount}
        visible={showConfirmModal}
        submitting={submitting}
      />

      <Table
        onRow={(record) => ({
          onClick: () => {
            selectedRecord(record);
          },
        })}
        rowClassName={() => 'ant-table-clickable-row'}
        columns={columns}
        dataSource={manageAccountsList}
        pagination={pagination}
        onChange={handleTableChange}
      />
    </Card>
  );
};

const mapStateToProps = (state) => ({
  manageAccountsList: state.manageAccounts.content,
  loading: state.loading.loading,
  totalElements: state.manageAccounts.totalElements,
  test: state.account.test,
  selectedAccount: state.account.selectedAccount,
  submitting: state.loading.submitting,
  preserveFilters: state.filterParams.preserveFilters,
});

const mapDispatchToProps = (dispatch) => ({
  fetchSelfAccounts: param => dispatch(manageAccountActions.fetchSelfAccounts({
    payload: param,
  })),
  updateSelfAccount: param => dispatch(manageAccountActions.updateSelfAccount({
    payload: param,
  })),
  deleteSelefAccount: param => dispatch(manageAccountActions.deleteSelefAccount({
    payload: param,
  })),
  addSelfAccount: param => dispatch(manageAccountActions.addSelfAccount({
    payload: param,
  })),
  addBusinessBankAccountAsPrimary: param => dispatch(manageAccountActions.addBusinessBankAccountAsPrimary({
    payload: param,
  })),
  setNotification: ({ type, payload }) => dispatch(notificationActions.setNotification({
    type,
    payload,
  })),
  setFilterData: (value) => dispatch(filterParamsActions.setFilterData({
    type: FILTERS_SPECS.SET_FILTER_DATA,
    payload: value,
  })),
});

export default connect(mapStateToProps, mapDispatchToProps)(ManageAccounts);
